import { InputProps } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useId, useState } from "react";

import { IconCalendar } from "@dfp/react-icons";

import "../Dayjs/dayjsIntegration";
import { FieldWrapper } from "./FieldWrapper";
import type { FieldBaseProps } from "./types";

const mapToISOString = (date: dayjs.Dayjs) => date.toISOString();

export const mapToShortISOString = (date: dayjs.Dayjs) =>
  date.format("YYYY-MM-DD");

export const mapToISOWithTZResetString = (date: dayjs.Dayjs) =>
  `${date.format("YYYY-MM-DD")}T00:00:00.000Z`;

// based on MUI DatePicker default props - https://mui.com/x/api/date-pickers/date-picker/#props
export const defaultMinDate = dayjs("1900-01-01");
export const defaultMaxDate = dayjs("2099-12-31");

export type FieldDatePickerProps = {
  placeholder?: string;
  startAdornment?: InputProps["startAdornment"];
  endAdornment?: InputProps["endAdornment"];
  mapDateToString?: (date: dayjs.Dayjs) => string;
  language: string;
  minDate?: Dayjs;
  maxDate?: Dayjs;
} & FieldBaseProps<string>;
export function FieldDatePicker({
  name,
  value,
  required,
  errorMessage,
  onChange,
  hidden,
  label,
  labelHelpText,
  helperText,
  readOnly,
  language,
  enableDeprecatedBottomGutter = false,
  mapDateToString = mapToISOString,
  minDate = defaultMinDate,
  maxDate = defaultMaxDate,
}: FieldDatePickerProps) {
  const labelId = useId();

  const [localValue, setLocalValue] = useState<dayjs.Dayjs | null>(
    value ? dayjs(value) : null,
  );

  // reset local value when value changes
  // this can happen when the form is reset,
  // or when field value is changed outside the field eg. depends on another field
  useEffect(() => {
    setLocalValue(value ? dayjs(value) : null);
  }, [value]);

  if (hidden === true) {
    return <input type="hidden" name={name} value={value} />;
  }

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    setLocalValue(date);

    onChange({
      target: {
        value: date && date.isValid() ? mapDateToString(date) : null,
      },
    });
  };

  return (
    <FieldWrapper
      labelId={labelId}
      errorMessage={errorMessage}
      label={label}
      labelHelpText={labelHelpText}
      helperText={helperText}
      readOnly={readOnly}
      required={required}
      name={name}
      helperTextProps={undefined}
      enableDeprecatedBottomGutter={enableDeprecatedBottomGutter}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
        <DatePicker<dayjs.Dayjs>
          value={localValue}
          format="L"
          disabled={readOnly}
          onChange={handleDateChange}
          slots={{ openPickerIcon: IconCalendar }}
          slotProps={{
            textField: {
              fullWidth: true,
              error: errorMessage ? true : undefined,
              inputProps: {
                "aria-labelledby": labelId,
                "data-testid": `field-${name}`,
              },
            },
          }}
          minDate={minDate}
          maxDate={maxDate}
        />
      </LocalizationProvider>
    </FieldWrapper>
  );
}
